import { apiUrl } from "config/envConfig";
import httpService from "services/httpService";

const BASE_URL = `${apiUrl}/payment-mgmt/admin/cashout`;

// Normalize cash out request objects from backend (support capitalized fields)
const normalizeCashOutRequest = (r) => {
  if (!r || typeof r !== 'object') return r;
  return {
    id: r.id ?? r.Id ?? null,
    driverId: r.driverId ?? r.DriverId ?? null,
    amount: (r.amount ?? r.Amount) != null ? Number(r.amount ?? r.Amount) : r.amount ?? null,
    status: r.status ?? r.Status ?? null,
    adminReason: r.adminReason ?? r.AdminReason ?? null,
    driverName: r.driverName ?? r.DriverName ?? null,
    walletTransactionId: r.walletTransactionId ?? r.WalletTransactionId ?? null,
    creationDate: r.creationDate ?? r.CreationDate ?? null,
    modificationDate: r.modificationDate ?? r.ModificationDate ?? null,
    driverWalletBalance: (r.driverWalletBalance ?? r.DriverWalletBalance) != null ? Number(r.driverWalletBalance ?? r.DriverWalletBalance) : r.driverWalletBalance ?? null,
    files: r.files ?? r.Files ?? [],
    raw: r,
  };
};

// =====================================================
// CREATE CASH OUT REQUEST
// =====================================================
export const createCashOutRequest = async (formData, onUploadProgress = null) => {
  try {
    const data = new FormData();
    data.append("walletId", formData.walletId);
    // support multiple driver creation: append repeated DriverIds fields
    if (formData.driverIds && Array.isArray(formData.driverIds) && formData.driverIds.length > 0) {
      formData.driverIds.forEach((id) => data.append("DriverIds", id));
    } else {
      data.append("driverId", formData.driverId || "");
    }

    data.append("amount", formData.amount);
    // ensure paymentMethod is sent as a numeric value (stringified) to match enum expectations
    data.append("paymentMethod", String(Number(formData.paymentMethod || 0)));
    data.append("createdByAdmin", String(Boolean(formData.createdByAdmin || true)));
    data.append("status", String(Number(formData.status || 1)));

    if (formData.files && formData.files.length > 0) {
      formData.files.forEach((file) => {
        data.append("files", file);
      });
    }
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    if (onUploadProgress && typeof onUploadProgress === "function") {
      config.onUploadProgress = onUploadProgress;
    }

    const response = await httpService.post(`${BASE_URL}/create`, data, config);
    return response.data;
  } catch (err) {
    console.error("Failed to create cash out request", err);
    throw err;
  }
};

// =====================================================
// GET ALL CASH OUT REQUESTS (with filters)
// =====================================================
export const getAllCashOutRequests = async (filter = {}) => {
  try {
    const payload = {
      pageNumber: filter.pageNumber && filter.pageNumber > 0 
        ? filter.pageNumber 
        : 1,

      pageSize: filter.pageSize && filter.pageSize > 0 
        ? filter.pageSize 
        : 10,

      driverId: filter.driverId ?? null,
      status: filter.status ?? null,
      from: filter.fromDate 
        ? new Date(filter.fromDate).toISOString() 
        : null,
      to: filter.toDate 
        ? new Date(filter.toDate).toISOString() 
        : null,
    };

    const response = await httpService.post(`${BASE_URL}/search`, payload);

    // Normalize various backend shapes into { items: [], totalCount }
    // Support both `data` and capitalized `Data`, and `page` / `Page` shapes
    const raw = response?.data?.data || response?.data?.Data || response?.data || {};

    let items = [];
    if (Array.isArray(raw.items)) items = raw.items;
    else if (Array.isArray(raw.page)) items = raw.page;
    else if (Array.isArray(raw.Page)) items = raw.Page;
    else if (Array.isArray(raw.list)) items = raw.list;
    else if (Array.isArray(raw)) items = raw;

    const normalizedItems = (items || []).map((it) => normalizeCashOutRequest(it));

    const totalCount = raw.totalCount || raw.totalRecords || raw.totalDisplayRecords || raw.TotalRecords || raw.TotalDisplayRecords || normalizedItems.length || 0;

    return { items: normalizedItems, totalCount };
  } catch (err) {
    console.error("Failed to fetch cash out requests", err);
    throw err;
  }
};

// =====================================================
// GET CASH OUT REQUEST BY ID
// =====================================================
export const getCashOutRequestById = async (id) => {
  try {
    const response = await httpService.get(`${BASE_URL}/${id}`);
    // Normalize wrapper (support capitalized `Data` too)
    const raw = response?.data?.data || response?.data?.Data || response?.data || null;
    return normalizeCashOutRequest(raw) || raw;
  } catch (err) {
    console.error(`Failed to fetch cash out request ${id}`, err);
    return null;
  }
};

// =====================================================
// APPROVE CASH OUT REQUEST
// =====================================================
export const approveCashOutRequest = (id, paymentMethod, files = [], onUploadProgress = null) => {
  const url = `${BASE_URL}/approve/${id}`;
  // If files provided, send multipart/form-data with files and paymentMethod
  if (files && files.length > 0) {
    const formData = new FormData();
    // some backends expect paymentMethod as string in form-data
    formData.append("paymentMethod", String(Number(paymentMethod || 1)));
    files.forEach((f) => formData.append("files", f));
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    if (onUploadProgress && typeof onUploadProgress === "function") config.onUploadProgress = onUploadProgress;
    return httpService.put(url, formData, config);
  }

  // Fallback: send JSON with numeric paymentMethod as an object. The
  // backend expects a model object for this route (not a raw number), so
  // send { paymentMethod: <number> } to avoid model-binding errors.
  return httpService.put(
    url,
    { paymentMethod: Number(paymentMethod || 1) },
    { headers: { "Content-Type": "application/json" } }
  );
};

// =====================================================
// REJECT CASH OUT REQUEST
// =====================================================
export const rejectCashOutRequest = async (id, reason) => {
  try {
    const payload = { adminReason: reason };
    const response = await httpService.put(`${BASE_URL}/reject/${id}`, payload);
    return response.data;
  } catch (err) {
    console.error(`Failed to reject cash out request ${id}`, err);
    throw err;
  }
};
